import { FormEvent, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useUpdateClientMutation } from "store/api/clientApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconHomeowner } from "assets/svg/modal-icon-edit-homeowner.svg"

import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter"
import { convertDataToOptions, convertEditDevicesToState } from "utils/convertData"
import { formatPhoneNumber } from "utils/formatPhoneNumber"
import getErrorMessage from "utils/getErrorMessage"
import {
  editCustomerCommonSchema,
  editCustomerDeviceSchema,
  onFormValidate,
  validationInitialState,
} from "utils/onValidate"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

import CustomerInformation from "./CustomerInformation"
import DeviceForm from "./DeviceForm"
import TabsWrapper from "./TabsWrapper"

import styles from "./index.module.scss"

const TabSettings = [
  { name: "Customer Info", value: "customerInfo" },
  { name: "Device(s)", value: "devices" },
]

export type CustomerCommonValues = {
  fullName: string
  homeownerID: number | undefined
  email: string
  phone: string
  membership: UITypes.Option[]
}

const commonValuesState: CustomerCommonValues = {
  fullName: "",
  homeownerID: undefined,
  email: "",
  phone: "",
  membership: [{ value: "no", label: "No" }] as UITypes.Option[],
}

const deviceValuesState: UITypes.Device = {
  deviceName: "",
  address1: "",
  address2: "",
  serialNumber: "",
  installationType: [],
  zip: [],
  country: [],
  state: [],
  city: [],
  suburb: [],
  residentialType: [],
  deviceLocation: [],
  systemAge: [],
}

interface Props {
  customer: ApiTypes.Model.ClientDevices
  showViewPort?: boolean
  onCancel: () => void
}

export const EditCustomerModal = ({ onCancel, customer, showViewPort }: Props) => {
  const [triggerUpdateClientQuery, { isLoading }] = useUpdateClientMutation()
  const dispatch = useAppDispatch()
  const [devices, setDevices] = useState<UITypes.Device[]>([])
  const [currentDevice, setCurrentDevice] = useState<number>(0)
  const [commonValues, setCommonValues] = useState(commonValuesState)
  const [deviceValues, setDeviceValues] = useState<UITypes.Device>(deviceValuesState)
  const currentZip = useAppSelector(state => state.tempData.currentZip)
  const [error, setError] = useState(validationInitialState)
  const [errorDeviceID, setErrorDeviceID] = useState<number | undefined>(undefined)
  const [activeTab, setActiveTab] = useState<string>("customerInfo")

  const onCancelClick = () => {
    dispatch(setCurrentZip(undefined))
    onCancel()
  }

  const onTabChange = (tab: number) => {
    if (tab === currentDevice) return
    saveChanges()
    setCurrentDevice(tab)
    if (!["phone", "email"].includes(error.field)) {
      setError(validationInitialState)
    }
    dispatch(setCurrentZip(undefined))
  }

  const handleViewPortClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    onCancel()
  }

  // UPDATE DEVICES STATE
  const saveChanges = () => {
    const _devices = devices?.map(obj => {
      if (obj?.id === deviceValues?.id) {
        return deviceValues
      }
      return obj
    })

    setDevices(_devices)
    return _devices
  }

  const onCommonValuesChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setCommonValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const getInstallationType = (device: UITypes.Device): string => {
    const type = device.installationType
    if (type) {
      if (typeof type === "string") {
        return type
      }

      return type[0]?.value as string
    }

    return ""
  }

  const prepareData = (devicesData: UITypes.Device[]) => {
    return {
      client: {
        full_name: commonValues.fullName,
        tel: commonValues.phone,
        email: commonValues.email,
        membership: commonValues.membership?.[0]?.value as string,
      },
      devices: devicesData?.map(item => {
        const installationType = getInstallationType(item)

        return {
          sn: item.serialNumber,
          name: item.deviceName || "",
          address1: item.address1,
          address2: item.address2,
          state: item.state?.[0] && Number(item.state?.[0]?.value),
          city: item.city?.[0] && Number(item.city?.[0]?.value),
          ...(item.suburb?.[0]?.value ? { suburb: item.suburb?.[0]?.value as string } : {}),
          country_id: item?.country?.[0] && Number(item?.country?.[0]?.value),
          zip_code: (item.zip?.[0] && item.zip?.[0]?.label) as string,
          where_installed_id: (item.deviceLocation?.[0] && Number(item.deviceLocation?.[0]?.value)) as number,
          resident_type_id: (item.residentialType?.[0] && Number(item.residentialType?.[0]?.value)) as number,
          installation_type: installationType,
          ...(item.systemAge?.[0] ? { system_age: Number(item.systemAge?.[0]?.value) } : {}),
        }
      }),
    }
  }

  // SEND REQUEST
  const sendRequest = async () => {
    try {
      const res = await triggerUpdateClientQuery({
        body: prepareData(saveChanges()),
        id: customer.id as number,
      })
      const error = getErrorMessage(res?.error)

      // @ts-expect-error - res.error is of type FetchBaseQueryError | SerializedError
      if (res?.error?.data?.errors?.["client.email"]) {
        // @ts-expect-error - res.error is of type FetchBaseQueryError | SerializedError
        setError({ field: "email", message: res.error.data.errors["client.email"] })
        return
      }

      if (res?.data) {
        toast.success("Customer has been successfully edited!")
        setCommonValues(commonValuesState)
        setDeviceValues(deviceValuesState)
        onCancel()
        dispatch(setCurrentZip(undefined))
      }
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSubmit = async (event: FormEvent<HTMLElement>) => {
    event.preventDefault()
    const _devices = saveChanges()

    // VALIDATION
    if (!onFormValidate(commonValues, editCustomerCommonSchema, setError)) return

    const isAllow = _devices?.every(item => {
      const result = onFormValidate(item, editCustomerDeviceSchema, setError)

      if (!result) {
        setErrorDeviceID(item?.id)
      } else {
        setErrorDeviceID(undefined)
      }

      return result
    })

    if (!isAllow) return

    sendRequest()
  }

  // SET CURRENT TAB
  useEffect(() => {
    if (!devices?.length) return
    setDeviceValues(devices[currentDevice])
  }, [currentDevice, devices])

  // SWITCH TAB WHEN VALIDATION ERROR IS HAPPEN
  useEffect(() => {
    if (errorDeviceID !== deviceValues?.id && error?.field) {
      const index = devices.findIndex(item => item?.id === errorDeviceID)
      if (index !== -1) {
        setCurrentDevice(index)
      }
    }

    if (error?.field) {
      if (["email", "phone"].includes(error?.field)) {
        setActiveTab("customerInfo")
      } else {
        setActiveTab("devices")
      }
    }
  }, [error, errorDeviceID])

  // FILL IN CITY AND STATE SELECT BASED ON ZIP
  useEffect(() => {
    if (currentZip?.city) {
      const city = convertDataToOptions({ data: [currentZip.city] }, "id", "name")
      const state = convertDataToOptions({ data: [currentZip.state] }, "id", "name")
      const country = convertDataToOptions({ data: [currentZip.country] }, "id", "name")
      const suburb = convertDataToOptions(
        { data: currentZip.suburbs?.length > 0 ? [currentZip.suburbs[0]] : null },
        "id",
        "name",
      )

      if (city && state) {
        setDeviceValues({
          ...deviceValues,
          ...{
            city,
            state,
            country,
            suburb,
          },
        })
      }
    }
  }, [currentZip])

  // INITIAL SET STATE
  useEffect(() => {
    setCommonValues({
      fullName: customer.full_name,
      homeownerID: customer.id,
      email: customer.email,
      phone: customer.phone_number ? formatPhoneNumber(customer.phone_number) : "",
      membership: [
        {
          value: customer.membership || "",
          label: capitalizeFirstLetter(customer.membership),
        },
      ],
    })

    if (customer.devices?.length) {
      const _devices = convertEditDevicesToState(customer.devices)
      setDevices(_devices)
      setDeviceValues(_devices[0])
    }
  }, [])

  const handleTransferSuccess = () => {
    if (devices.length === 1) {
      onCancel()
      return
    }

    setDevices(devices.filter(device => device.serialNumber !== deviceValues.serialNumber))
    setDeviceValues(devices[0])
  }

  return (
    <>
      {showViewPort && <div className={styles.transferModalViewport} onClick={handleViewPortClick}></div>}
      {createPortal(
        <ModalLayout
          width="1252px"
          confirmButtonText="Save"
          confirmButtonType="submit"
          onConfirm={onSubmit}
          onCancel={onCancelClick}
          buttonSize="auto"
          footerClassName={styles.modalFooter}
          isFetching={isLoading}
        >
          <ModalHeader title={`Edit Customer ${commonValues.homeownerID}`}>
            <IconHomeowner />
          </ModalHeader>
          <div className={styles.tabButtonsWrapper}>
            {TabSettings.map(tab => (
              <span
                key={tab.value}
                className={classNames(styles.tabButton, { [styles.active]: activeTab === tab.value })}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.name}
              </span>
            ))}
          </div>
          <div className={styles.modalContent}>
            <div className={styles.modalContentWrapper}>
              <CustomerInformation
                customer={customer}
                commonValues={commonValues}
                error={error}
                onCommonValuesChange={onCommonValuesChange}
                setCommonValues={setCommonValues}
                hideTab={activeTab === "devices"}
              />
              <main className={classNames(styles.modalMain, { [styles.hideTab]: activeTab === "customerInfo" })}>
                <TabsWrapper
                  devices={devices}
                  currentDevice={currentDevice}
                  onTabChange={onTabChange}
                  activeTab={activeTab}
                />
                <DeviceForm
                  deviceValues={deviceValues}
                  commonValues={commonValues}
                  error={error}
                  onSetDeviceValues={setDeviceValues}
                  onSetCommonValues={setCommonValues}
                  onHandleTransferSuccess={handleTransferSuccess}
                />
              </main>
            </div>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
